/**
 * Feature Flag Service
 *
 * Abstracción agnóstica sobre el proveedor de feature flags (actualmente PostHog).
 * Para añadir un nuevo flag: agregar la constante en FLAGS y usarla en el código.
 * Para cambiar de proveedor: implementar createXxxProvider y pasarlo a createFeatureFlagService.
 */

// ─── Catálogo de flags y sus variantes ───────────────────────────────────────
// Cada flag declara su nombre (key) y las variantes que puede devolver PostHog.
// Convención: CONTROL es siempre el comportamiento por defecto (rama segura).
//
// Para añadir un nuevo flag:
//   1. Agregar la entrada aquí con su nombre y variantes.
//   2. Usar FLAG.MI_NUEVO_FLAG.key  →  nombre del flag para el provider
//      Usar FLAG.MI_NUEVO_FLAG.VARIANTE  →  valor a comparar en el código
export const FLAG = Object.freeze({
    AB_TEST_BOTON_QQMC: {
        key: 'ab-test-boton-qqmc',
        CONTROL: 'control',
        ME_INTERESA: 'me-interesa',
    },
    ABC_TEST_NUEVO_FLUJO_DE_AGENDA: {
        key: 'ab-test-nuevo-flujo-de-agenda',
        CONTROL: 'control',
        HORARIO_VISIBLE: 'horario-visible',
    },
    // Ejemplo de un segundo flag:
    // AB_TEST_HEADER_CTA: {
    //     key: 'ab-test-header-cta',
    //     CONTROL:      'control',
    //     VARIANT_BOLD: 'bold-cta',
    // },
});

// ─── Interfaz del provider ────────────────────────────────────────────────────
// Todo provider debe implementar: isEnabled(flag) y getVariant(flag, fallback).

/**
 * Provider real usando la instancia de PostHog (named instance o default).
 *
 * @param {object} posthogInstance - posthog o posthog['nombre_instancia']
 */
export const createPosthogProvider = (posthogInstance) => ({
    // onReady: espera a que PostHog haya cargado los flags antes de ejecutar el callback.
    onReady(cb) { posthogInstance.onFeatureFlags(cb); },
    isEnabled(flagName) {
        try {
            return posthogInstance.isFeatureEnabled(flagName) ?? false;
        } catch {
            return false;
        }
    },
    getVariant(flagName, fallback = 'control') {
        try {
            return posthogInstance.getFeatureFlag(flagName) ?? fallback;
        } catch {
            return fallback;
        }
    },
});

/**
 * Provider de consola: loguea las llamadas y devuelve el fallback.
 * Útil en desarrollo para ver qué flags se consultan sin enviar datos a PostHog.
 */
export const createConsoleProvider = () => ({
    // onReady: los flags están "listos" de inmediato (no hay red que esperar).
    onReady(cb) { cb(); },
    isEnabled(flagName) {
        console.log(`[FeatureFlags] isEnabled("${flagName}") → false (console provider)`);
        return false;
    },
    getVariant(flagName, fallback = 'control') {
        console.log(`[FeatureFlags] getVariant("${flagName}") → "${fallback}" (console provider)`);
        console.log(fallback)
        return fallback;
    },
});

/**
 * Crea el servicio de feature flags con el provider dado.
 *
 * @param {object} provider
 * @returns {{ isEnabled(flag: object): boolean, getVariant(flag: object, fallback?: string): string }}
 *
 * @example
 * import { createFeatureFlagService, createPosthogProvider, FLAG } from './feature-flags';
 *
 * const featureFlags = createFeatureFlagService(createPosthogProvider(posthog));
 *
 * // Variante de un A/B test — el flag y sus variantes viven juntos
 * const variant = featureFlags.getVariant(FLAG.AB_TEST_BOTON_QQMC);
 * if (variant === FLAG.AB_TEST_BOTON_QQMC.ME_INTERESA) { ... }
 *
 * // Flag booleano
 * if (featureFlags.isEnabled(FLAG.AB_TEST_BOTON_QQMC)) { ... }
 */
export const createFeatureFlagService = (provider) => ({
    // onReady: ejecuta el callback cuando los flags están disponibles.
    // En producción (PostHog) espera la red; en desarrollo/tests llama inmediatamente.
    onReady: (cb) => provider.onReady(cb),
    isEnabled: (flag) => provider.isEnabled(typeof flag === 'string' ? flag : flag.key),
    getVariant: (flag, fallback) => {
        const key = typeof flag === 'string' ? flag : flag.key;
        const defaultFallback = typeof flag === 'string' ? 'control' : flag.CONTROL;
        return provider.getVariant(key, fallback ?? defaultFallback);
    },
});
